import dayjs from 'dayjs'
import React from 'react'

import { OVERLAP_PADDING } from './commonStyles'
import { ICalendarEvent, Mode, WeekNum } from './interfaces'

export const typedMemo: <T>(c: T) => T = React.memo

export const DAY_MINUTES = 1440

export function getDatesInMonth(date: Date | dayjs.Dayjs = new Date(), locale = 'en') {
  const subject = dayjs(date)
  const days = Array(subject.daysInMonth() - 1)
    .fill(0)
    .map((_, i) => {
      return subject.date(i + 1).locale(locale)
    })
  return days
}

export function getDatesInWeek(
  date: Date | dayjs.Dayjs = new Date(),
  weekStartsOn: WeekNum = 0,
  locale = 'en',
) {
  const subject = dayjs(date)
  const subjectDOW = subject.day()
  const days = Array(7)
    .fill(0)
    .map((_, i) => {
      return subject
        .add(i - (subjectDOW < weekStartsOn ? 7 + subjectDOW : subjectDOW) + weekStartsOn, 'day')
        .locale(locale)
    })
  return days
}

export function getDatesInNextThreeDays(date: Date | dayjs.Dayjs = new Date(), locale = 'en') {
  const subject = dayjs(date).locale(locale)
  const days = Array(3)
    .fill(0)
    .map((_, i) => {
      return subject.add(i, 'day')
    })
  return days
}

export function getDatesInNextOneDay(date: Date | dayjs.Dayjs = new Date(), locale = 'en') {
  const subject = dayjs(date).locale(locale)
  const days = Array(1)
    .fill(0)
    .map((_, i) => {
      return subject.add(i, 'day')
    })
  return days
}

export const hours = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
]

export function formatHour(hour: number, ampm = false) {
  if (ampm) {
    if (hour === 0) {
      return ''
    }
    if (hour === 12) {
      return `12 PM`
    }
    if (hour > 12) {
      return `${hour - 12} PM`
    }
    return `${hour} AM`
  }
  return `${hour}:00`
}

export function isToday(date: dayjs.Dayjs) {
  const today = dayjs()
  return today.isSame(date, 'day')
}

export function getRelativeTopInDay(date: dayjs.Dayjs) {
  return (100 * (date.hour() * 60 + date.minute())) / DAY_MINUTES
}

export function todayInMinutes() {
  const today = dayjs()
  return today.diff(dayjs().startOf('day'), 'minute')
}

export function modeToNum(mode: Mode, current?: dayjs.Dayjs | Date): number {
  if (mode === 'month') {
    if (!current) {
      throw new Error('You must specify current date if mode is month')
    }
    if (current instanceof Date) {
      current = dayjs(current)
    }
    return current.daysInMonth() - current.date() + 1
  }
  switch (mode) {
    case 'day':
      return 1
    case '3days':
      return 3
    case 'week':
    case 'custom':
      return 7
    default:
      throw new Error('undefined mode')
  }
}

export function formatStartEnd(start: Date, end: Date, format: string) {
  return `${dayjs(start).format(format)} - ${dayjs(end).format(format)}`
}

export function isAllDayEvent(start: Date, end: Date, event: any) {

  let eventObject = getTaskOfObject(event)
  if (eventObject && eventObject.isAllDay)
    return true
  const _start = dayjs(start)
  const _end = dayjs(end)
  const lengthInDays = _end.diff(_start) / 86400000
  var result = false
  if (lengthInDays >= 0.99998)
    result = true
  return result
}

export function getCountOfEventsAtEvent(
  event: ICalendarEvent<any>,
  eventList: ICalendarEvent<any>[],
) {
  return eventList.filter(
    (e) =>
      dayjs(event.start).isBetween(e.start, e.end, 'minute', '[)') ||
      dayjs(e.start).isBetween(event.start, event.end, 'minute', '[)'),
  ).length
}

export function getOrderOfEvent(event: ICalendarEvent<any>, eventList: ICalendarEvent<any>[]) {
  const events = eventList
    .filter(
      (e) =>
        dayjs(event.start).isBetween(e.start, e.end, 'minute', '[)') ||
        dayjs(e.start).isBetween(event.start, event.end, 'minute', '[)'),
    )
    .sort((a, b) => {
      if (dayjs(a.start).isSame(b.start)) {
        return dayjs(a.start).diff(a.end) < dayjs(b.start).diff(b.end) ? -1 : 1
      } else {
        return dayjs(a.start).isBefore(b.start) ? -1 : 1
      }
    })
  const index = events.indexOf(event)
  return index === -1 ? 0 : index
}

export function getStyleForOverlappingEvent(eventPosition: number, overlapOffset: number) {
  let overlapStyle = {}
  const offset = overlapOffset
  const start = eventPosition * offset
  const zIndex = 100 + eventPosition + start * 0
  overlapStyle = {
    start: 0 + 50,
    end: OVERLAP_PADDING,
    zIndex,
  }
  return overlapStyle
}

export function getDatesInNextCustomDays(
  date: Date | dayjs.Dayjs = new Date(),
  weekStartsOn: WeekNum = 0,
  weekEndsOn: WeekNum = 6,
  locale = 'en',
) {
  const subject = dayjs(date)
  const subjectDOW = subject.day()
  const days = Array(weekDaysCount(weekStartsOn, weekEndsOn))
    .fill(0)
    .map((_, i) => {
      return subject.add(i - subjectDOW + weekStartsOn, 'day').locale(locale)
    })
  return days
}

// TODO: This method should be unit-tested
function weekDaysCount(weekStartsOn: WeekNum, weekEndsOn: WeekNum) {
  // handle reverse week
  if (weekEndsOn < weekStartsOn) {
    let daysCount = 1
    let i = weekStartsOn
    while (i !== weekEndsOn) {
      ++i
      ++daysCount
      if (i > 6) {
        i = 0
      }
      // fallback for infinite
      if (daysCount > 7) {
        break
      }
    }
    return daysCount
  }
  // normal week
  if (weekEndsOn > weekStartsOn) {
    return weekEndsOn - weekStartsOn + 1
  }
  // default
  return 1
}

export function transformToPersianNumbers(en_number: string) {
  let persianDigits = '۰۱۲۳۴۵۶۷۸۹'
  let persianMap = persianDigits.split('')
  let persian_number = en_number.replace(/\d/g, function (m) {
    return persianMap[parseInt(m)]
  })
  return persian_number
}

export function calculatePosition(date: dayjs.Dayjs, dateRange: dayjs.Dayjs[], layout) {
  //hours column width is 50 pixel
  const realWidth = layout.width - 50;
  let a = 0
  for (let i = 0; i < dateRange.length; i++) {
    if (dateRange[i].day() == date.day())
      a = i
  }
  const leftIndex = a
  const relativeX = (leftIndex * realWidth) / 7
  const x = 50 + relativeX
  const y = (1000 / 24) * date.hour()
  return { x, y }
}

export function getWidthOfAllDayInPercent(start: Date, end: Date) {
  const diffInMiliSeconds = dayjs(end).diff(dayjs(start))
  const diffInDays = diffInMiliSeconds / 86400000
  return diffInDays * 97
}

export function getTaskOfObject(e: any) {
  let eventObject
  try {
    eventObject = JSON.parse(e.task)
  } catch (error) {
    eventObject = e.task
  }
  return eventObject
}

const appendZero = (hexaValue) => {
  while (hexaValue.length < 2) hexaValue = '0' + hexaValue
  return hexaValue
}

export const calculateWeightedColor = (dayEvents: any) => {
  let totalDuration = 0
  let totalR = 0
  let totalG = 0
  let totalB = 0
  dayEvents.forEach((element) => {
    const r = parseInt(element.color.substring(0, 2), 16) * element.duration
    const g = parseInt(element.color.substring(2, 4), 16) * element.duration
    const b = parseInt(element.color.substring(4, 6), 16) * element.duration
    totalR = totalR + r
    totalG = totalG + g
    totalB = totalB + b
    totalDuration = totalDuration + element.duration
  })
  const meanR = Math.round(totalR / totalDuration)
  const meanG = Math.round(totalG / totalDuration)
  const meanB = Math.round(totalB / totalDuration)
  const weightedColor =
    '#' +
    appendZero(meanR.toString(16).toUpperCase()) +
    appendZero(meanG.toString(16).toUpperCase()) +
    appendZero(meanB.toString(16).toUpperCase())
  return weightedColor
}

export const isTaskBelongsDay = (start, end, date) => {
  dayjs(start).isBetween(date.startOf('day'), date.endOf('day'), null, '[)') ||
    dayjs(end).isBetween(date.startOf('day'), date.endOf('day'), null, '[)') ||
    (dayjs(start).isBefore(date.startOf('day')) && dayjs(end).isAfter(date.endOf('day')))
}